import { useRef, useMemo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useFBO } from "@react-three/drei";
import * as THREE from "three";

// Custom shader material for the fluid effect
const simulationMaterial = new THREE.ShaderMaterial({
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    varying vec2 vUv;
    uniform sampler2D uCurrentPosition;
    uniform sampler2D uOriginalPosition;
    uniform float uTime;
    uniform float uCurl;
    uniform float uSpeed;

    vec3 snoise(vec3 uv) {
      uv.x += uTime * 0.15;
      float s = sin(uv.z * 3.5) * 0.3 + cos(uv.y * 4.2) * 0.4 + sin(uv.x * 3.8) * 0.3;
      float c = cos(uv.z * 3.2) * 0.3 + sin(uv.y * 4.5) * 0.4 + cos(uv.x * 3.5) * 0.3;
      float s2 = sin(uv.y * 2.8) * 0.3 + cos(uv.x * 3.5) * 0.4 + sin(uv.z * 2.5) * 0.3;
      float c2 = cos(uv.y * 3.0) * 0.3 + sin(uv.x * 4.0) * 0.4 + cos(uv.z * 2.8) * 0.3;
      return vec3(s, c, s2 * c2) * uCurl;
    }

    void main() {
      vec3 currentPos = texture2D(uCurrentPosition, vUv).xyz;
      vec3 originalPos = texture2D(uOriginalPosition, vUv).xyz;
      vec3 noise = snoise(currentPos * 0.1);
      currentPos += noise * uSpeed;
      gl_FragColor = vec4(currentPos, 1.0);
    }
  `,
  uniforms: {
    uCurrentPosition: { value: null },
    uOriginalPosition: { value: null },
    uTime: { value: 0 },
    uCurl: { value: 2.8 },
    uSpeed: { value: 0.08 },
  },
});

const renderMaterial = new THREE.ShaderMaterial({
  vertexShader: `
    uniform sampler2D uPosition;
    uniform float uTime;
    varying vec3 vColor;

    void main() {
      vec3 pos = texture2D(uPosition, position.xy).xyz;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      gl_PointSize = 1.8;
      
      // Shape-shifting DMT-inspired color mutation
      vec3 baseColor = normalize(pos) * 0.5 + 0.5;
      
      float t = uTime * 8.0;
      float r = sin(t * 2.5 + baseColor.x * 10.0) * 0.5 + 0.5;
      float g = sin(t * 3.2 + baseColor.y * 9.0 + 1.8) * 0.5 + 0.5;
      float b = sin(t * 2.1 + baseColor.z * 11.0 + 3.5) * 0.5 + 0.5;
      
      vColor = vec3(r, g, b);
    }
  `,
  fragmentShader: `
    varying vec3 vColor;
    void main() {
      gl_FragColor = vec4(vColor, 1.0);
    }
  `,
  uniforms: {
    uPosition: { value: null },
    uTime: { value: 0 },
  },
});

export function HeroScene() {
  const size = 512;
  const pointsRef = useRef<THREE.Points>(null!);
  const { gl } = useThree();

  // Create FBOs
  const fbo1 = useFBO(size, size, {
    type: THREE.FloatType,
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
  });
  const fbo2 = useFBO(size, size, {
    type: THREE.FloatType,
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
  });

  // Initialize particle positions and textures
  const { originalPositionTexture, particlePositions } = useMemo(() => {
    const particles = new Float32Array(size * size * 4);
    const geometry = new THREE.TorusKnotGeometry(1.2, 0.3, 400, 32);
    const positions = geometry.attributes.position.array as Float32Array;

    for (let i = 0; i < size * size; i++) {
      const i4 = i * 4;
      const p_i = (i * 3) % positions.length;
      particles[i4 + 0] = positions[p_i + 0];
      particles[i4 + 1] = positions[p_i + 1];
      particles[i4 + 2] = positions[p_i + 2];
      particles[i4 + 3] = 1.0;
    }

    const originalPositionTexture = new THREE.DataTexture(
      particles,
      size,
      size,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    originalPositionTexture.needsUpdate = true;

    const tempScene = new THREE.Scene();
    const tempCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const tempMaterial = new THREE.MeshBasicMaterial({ map: originalPositionTexture });
    const tempMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), tempMaterial);
    tempScene.add(tempMesh);

    gl.setRenderTarget(fbo1);
    gl.render(tempScene, tempCamera);
    gl.setRenderTarget(null);

    const particlePositions = new Float32Array(size * size * 3);
    for (let i = 0; i < size * size; i++) {
      const i3 = i * 3;
      particlePositions[i3 + 0] = (i % size) / size;
      particlePositions[i3 + 1] = Math.floor(i / size) / size;
      particlePositions[i3 + 2] = 0;
    }

    return { originalPositionTexture, particlePositions };
  }, [size, gl, fbo1]);

  // Simulation loop
  useFrame((state) => {
    const { gl, clock } = state;
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    simulationMaterial.uniforms.uCurrentPosition.value = fbo1.texture;
    simulationMaterial.uniforms.uOriginalPosition.value = originalPositionTexture;
    simulationMaterial.uniforms.uTime.value = clock.elapsedTime;

    const simulationMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), simulationMaterial);
    scene.add(simulationMesh);

    gl.setRenderTarget(fbo2);
    gl.render(scene, camera);
    gl.setRenderTarget(null);

    const temp = fbo1.texture;
    fbo1.texture = fbo2.texture;
    fbo2.texture = temp;

    renderMaterial.uniforms.uPosition.value = fbo1.texture;
    renderMaterial.uniforms.uTime.value = clock.elapsedTime;

    if (pointsRef.current) {
      pointsRef.current.rotation.y += 0.012;
      pointsRef.current.rotation.x += 0.006;
    }
  });

  return (
    <>
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={size * size}
            array={particlePositions}
            itemSize={3}
          />
        </bufferGeometry>
        <primitive object={renderMaterial} attach="material" />
      </points>
    </>
  );
}
